/**
 * SelectQuestion Component
 * 
 * Handles single-select and multi-select questions.
 * Used for:
 * - Service type selection
 * - All branching questions
 * - Micro goals, security concerns, etc.
 * 
 * Features:
 * - Large, touch-friendly selection buttons
 * - Immediate feedback on selection
 * - Support for icons and descriptions
 */

import React from 'react';
import { motion } from 'framer-motion';
import { SelectionGroup } from '../UI/SelectionButton';
import { Question } from '../../types/funnel.types';
import { trackOptionSelected } from '../../utils/analytics';

// ============================================
// TYPES
// ============================================

interface SelectQuestionProps {
  /** Question data */
  question: Question;
  /** Current selected value(s) */
  value: string | string[] | undefined;
  /** Change handler */
  onChange: (value: string | string[]) => void;
  /** Auto-advance on selection (for single-select) */
  autoAdvance?: boolean;
  /** Handler called when user makes selection (for auto-advance) - receives the selected value */
  onSelect?: (selectedValue: string | string[]) => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * SelectQuestion - Handles selection-based questions
 */
export const SelectQuestion: React.FC<SelectQuestionProps> = ({
  question,
  value,
  onChange,
  autoAdvance = true,
  onSelect,
}) => {
  // Handle option selection
  const handleSelect = (optionId: string) => {
    const selectedOption = question.options?.find(opt => opt.id === optionId);
    
    // Track selection for analytics
    if (selectedOption) {
      trackOptionSelected(question.id, optionId, selectedOption.label);
    }

    if (question.type === 'multi-select') {
      // Multi-select: toggle the option in the array
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionId)
        ? currentValues.filter(v => v !== optionId)
        : [...currentValues, optionId];
      onChange(newValues);
    } else {
      // Single-select: replace the value
      onChange(optionId);
      
      // Auto-advance after a short delay (for visual feedback)
      // Pass the selected value directly to avoid state timing issues
      if (autoAdvance && onSelect) {
        setTimeout(() => {
          onSelect(optionId);
        }, 300);
      }
    }
  };

  // Determine grid columns based on option count and descriptions
  const getColumnCount = (): 1 | 2 | 3 => {
    const optionCount = question.options?.length || 0;
    const hasDescriptions = question.options?.some(opt => opt.description);
    
    // If options have descriptions, use single column for readability
    if (hasDescriptions) return 1;
    
    // Otherwise, use 2 columns for 4+ options
    if (optionCount >= 4) return 2;
    
    return 1;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
    >
      {question.options && (
        <SelectionGroup
          options={question.options}
          value={value}
          onChange={handleSelect}
          multiple={question.type === 'multi-select'}
          columns={getColumnCount()}
        />
      )}

      {/* Helper text for multi-select */}
      {question.type === 'multi-select' && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-4 text-sm text-text-secondary text-center"
        >
          Select all that apply
        </motion.p>
      )}
    </motion.div>
  );
};

SelectQuestion.displayName = 'SelectQuestion';

export default SelectQuestion;
